import React from 'react';
import Icon from './Icon';

// An illustrative workspace, shaped like the app's data-sources screen. The
// selected source opens to show what Sidwell read from one of its tables —
// the same column roles explained further down the Company Data Cloud page.
const SOURCE_STATE = {
  POSTGRESQL: {
    status: 'Connected',
    mode: 'Live · read-only session',
    size: '14 tables · 2.1M rows',
    checked: 'Checked 2 min ago',
    table: 'gl_transactions',
    columns: [
      { name: 'department', role: 'Category' },
      { name: 'amount_usd', role: 'Amount' },
      { name: 'posted_at', role: 'Date' },
    ],
    footnote: { icon: 'lock', text: 'Password encrypted — never shown again' },
  },
  MYSQL: {
    status: 'Connected',
    mode: 'Live connection',
    size: '9 tables · 640k rows',
    checked: 'Checked 5 min ago',
    table: 'orders',
    columns: [
      { name: 'region', role: 'Category' },
      { name: 'order_total', role: 'Amount' },
      { name: 'order_date', role: 'Date' },
    ],
    footnote: { icon: 'lock', text: 'Password encrypted — never shown again' },
  },
  'CSV & XLSX': {
    status: 'Uploaded',
    mode: 'File upload',
    size: '3 files · 48k rows',
    checked: 'Added yesterday',
    table: 'budget_fy26.xlsx',
    columns: [
      { name: 'cost_centre', role: 'Category' },
      { name: 'budget', role: 'Amount' },
      { name: 'month', role: 'Date' },
    ],
    footnote: { icon: 'shield', text: 'Locked to your firm' },
  },
};

const iconFor = (name) => (/CSV|EXCEL|XLSX/.test(name) ? 'file' : 'database');

function SourceConnectionCard({ sources = [], selected, onSelect }) {
  const shown = sources.filter((name) => SOURCE_STATE[name]);

  return (
    <div className="statement relative w-full shadow-raised">
      <div className="flex items-baseline justify-between gap-4 px-5 pb-3 pt-4">
        <span className="label">Connected sources</span>
        {/* The figures below are illustrative, not a real customer's. */}
        <span className="text-[13px] font-medium text-glass">Example workspace</span>
      </div>

      <ul className="m-0 list-none border-t border-wall/70 p-0">
        {shown.map((name) => {
          const source = SOURCE_STATE[name];
          const active = name === selected;
          return (
            <li key={name} className="border-t border-stone first:border-t-0">
              <button
                type="button"
                onClick={() => onSelect && onSelect(name)}
                aria-pressed={active}
                className={`relative block min-h-[48px] w-full px-5 py-3 text-left transition-colors ${
                  active ? 'bg-limestone' : 'hover:bg-limestone/60'
                }`}
              >
                <span aria-hidden="true" className={`absolute inset-y-0 left-0 w-[3px] ${active ? 'bg-bronze' : 'bg-transparent'}`} />
                <span className="flex items-center justify-between gap-3">
                  <span className="flex min-w-0 items-center gap-2.5 font-semibold">
                    <Icon name={iconFor(name)} size={17} className="text-glass" />
                    <span className="truncate text-[15px]">{name}</span>
                  </span>
                  <span className="inline-flex shrink-0 items-center gap-1.5 text-[13px] font-medium text-patina">
                    <span aria-hidden="true" className="h-1.5 w-1.5 rounded-full bg-patina" />
                    {source.status}
                  </span>
                </span>
                <span className="mt-1 flex flex-wrap gap-x-3 gap-y-0.5 pl-[27px] text-[13px] text-glass figure">
                  <span>{source.mode}</span>
                  <span>{source.size}</span>
                  <span>{source.checked}</span>
                </span>
              </button>

              {active && (
                <div className="swap-in border-t border-stone bg-white px-5 py-3">
                  <div className="mb-2 text-[13px] font-medium text-glass">
                    What Sidwell sees in <span className="font-mono text-wall">{source.table}</span>
                  </div>
                  <ul className="m-0 list-none p-0">
                    {source.columns.map((column) => (
                      <li key={column.name} className="flex items-center justify-between gap-3 border-t border-stone py-1.5 text-[13px]">
                        <span className="font-mono">{column.name}</span>
                        <span className="font-medium text-glass">{column.role}</span>
                      </li>
                    ))}
                  </ul>
                  <div className="mt-1 flex items-center gap-2 border-t border-stone pt-2 text-[13px] text-glass">
                    <Icon name={source.footnote.icon} size={15} />
                    {source.footnote.text}
                  </div>
                </div>
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
}

export default SourceConnectionCard;
